import type { TFunction } from 'i18next';
import { ApiError } from '@/lib/api';
import { canonicalizePermission } from '@/stores/authStore';

function quoteRequestErrorMessage(error: unknown, t: TFunction): string | undefined {
  if (error instanceof TypeError) return t('acceptQuote.outcomeUnknown');
  if (!(error instanceof ApiError)) return undefined;
  if (error.status === 412) return t('acceptQuote.changedSinceReview');
  const data = error.data as { code?: string; error?: string } | undefined;
  const code = data?.code ?? data?.error;
  if (code === 'IDEMPOTENCY_OUTCOME_UNKNOWN' || error.status >= 500) {
    return t('acceptQuote.outcomeUnknown');
  }
  if (code?.startsWith('QUOTE_VERSION_')) return t('quoteHistory.restoreFailed');
  if (code === 'UNSUPPORTED_QUOTE_REVIEW_SOURCE') return t('quoteHistory.restoreFailed');
  if (code === 'IDEMPOTENCY_IN_PROGRESS') return t('acceptQuote.inProgress');
  return undefined;
}

/** `t` is the workorders namespace. Unknown outcomes must not claim failure. */
export function acceptQuoteErrorMessage(error: unknown, t: TFunction): string {
  const common = quoteRequestErrorMessage(error, t);
  if (common) return common;
  if (error instanceof ApiError) {
    const data = error.data as { required?: string[]; message?: string } | undefined;
    // Old servers name `bookings.assign`, new ones `work_orders.assign` (#3307).
    if (
      error.status === 403 &&
      Array.isArray(data?.required) &&
      data.required.some((id) => canonicalizePermission(id) === 'work_orders.assign')
    ) {
      return t('acceptQuote.assignForbidden');
    }
    if (error.status === 409 && data?.message === 'Lift is not available during the scheduled time') {
      return t('acceptQuote.liftConflict');
    }
  }
  return t('detail.statusChangeFailed');
}

export function rejectQuoteErrorMessage(error: unknown, t: TFunction): string {
  return quoteRequestErrorMessage(error, t) ?? t('detail.statusChangeFailed');
}
